import { Attribute } from '@/types/entity/product';

// EWM-198 — parses the CT `productFeatures` attribute into the flat
// `Attribute[]` shape consumed by the PDP specifications zebra table.
//
// Aggregation rules layered on top of the default flatten:
// - **Multivalued.** When multiple features share the same `code` within a
//   group, collapse to one row. Range features (`isrange: true`) render as
//   "min - max <unit>"; other multivalued features render as
//   "v1, v2, …, vN <unit>" preserving CT's declared order.
// - **Physical_PROD.** When `prodLength` + `prodHeight` + `prodDepth` are all
//   present, synthesize a single row labelled by the group's
//   `classifyingcategory` (e.g. "Product Dimensions") formatted
//   "L x H x D <unit>". Weight + any other Physical_PROD features (cable
//   length, diameter, …) fall through to the default path.
// - **Prod_PACKAGED.** When `Packaged_LENGTH` + `Packaged_WIDTH` +
//   `Packaged_HEIGHT` are all present, synthesize a row labelled by the
//   group's `classifyingcategory` (e.g. "Packaged Dimensions") as
//   "L x W x H <unit>". Pre-formatted single-row variants (e.g. CT product
//   WC7766 stores it as one STRING feature) fall through unchanged.
// - **Ordering.** Within each group, codes are emitted in ascending
//   `featureposition` order. Groups are emitted in CT's declared order.
//
// Synthesized row labels come from the CT data (`classifyingcategory`) — no
// hardcoded user-facing strings, no new i18n keys.

type RawFeature = {
  name?: unknown;
  value?: unknown;
  unit?: unknown;
  code?: unknown;
  featureposition?: unknown;
  isrange?: unknown;
};

type RawGroup = {
  categorycode?: unknown;
  classifyingcategory?: unknown;
  features?: RawFeature[];
};

const PHYSICAL_PROD = 'Physical_PROD';
const PROD_PACKAGED = 'Prod_PACKAGED';

const isScalar = (v: unknown): v is string | number => typeof v === 'string' || typeof v === 'number';

const featureUnit = (f: RawFeature): string => (typeof f.unit === 'string' && f.unit ? f.unit : '');

const featureValue = (f: RawFeature): string | number | undefined => {
  if (!isScalar(f.value)) return undefined;
  if (f.value === '') return undefined;
  return f.value;
};

// Default formatting: value and unit are concatenated tight ("1.5m", "14.4V").
// The L×W×H dimension synthesizers in renderPhysicalProd / renderProdPackaged
// use their own inline format that keeps a space before the unit
// ("151 x 95 x 65 mm") for readability.
const formatValueWithUnit = (value: string | number, unit: string): string => (unit ? `${value}${unit}` : `${value}`);

// Group features by `code`, preserving first-seen order.
const groupByCode = (features: RawFeature[]): Map<string, RawFeature[]> => {
  const map = new Map<string, RawFeature[]>();
  for (const f of features) {
    const code = typeof f.code === 'string' && f.code ? f.code : '__no_code__';
    const arr = map.get(code) ?? [];
    arr.push(f);
    map.set(code, arr);
  }
  return map;
};

// Collapse a same-code feature list into one row. Each value carries its own
// unit ("14.4 V, 15 V"); range features render "9 mm - 32 mm" so the unit
// stays attached when reading min/max independently.
const aggregateSameCode = (features: RawFeature[]): Attribute | undefined => {
  const first = features[0];
  if (!first) return undefined;
  const name = typeof first.name === 'string' && first.name ? first.name : undefined;
  if (!name) return undefined;
  const formatted = features
    .map((f) => {
      const v = featureValue(f);
      return v === undefined ? undefined : formatValueWithUnit(v, featureUnit(f));
    })
    .filter((s): s is string => s !== undefined);
  if (formatted.length === 0) return undefined;
  if (formatted.length === 1) return { label: name, value: formatted[0] };
  const isRange = features.some((f) => f.isrange === true);
  const joined = isRange ? `${formatted[0]} - ${formatted[formatted.length - 1]}` : formatted.join(', ');
  return { label: name, value: joined };
};

// Default rendering for a category: group by code, aggregate multivalued,
// sort by the earliest featureposition each code occupies.
const renderDefault = (features: RawFeature[], excludeCodes: ReadonlySet<string> = new Set()): Attribute[] => {
  const filtered =
    excludeCodes.size === 0
      ? features
      : features.filter((f) => !excludeCodes.has(typeof f.code === 'string' ? f.code : ''));
  const grouped = groupByCode(filtered);
  const entries: Array<{ position: number; row: Attribute }> = [];
  for (const group of grouped.values()) {
    const row = aggregateSameCode(group);
    if (!row) continue;
    const position = group.reduce((min, f) => {
      const p = typeof f.featureposition === 'number' ? f.featureposition : Number.MAX_SAFE_INTEGER;
      return p < min ? p : min;
    }, Number.MAX_SAFE_INTEGER);
    entries.push({ position, row });
  }
  entries.sort((a, b) => a.position - b.position);
  return entries.map((e) => e.row);
};

const firstWithValue = (
  byCode: Map<string, RawFeature[]>,
  code: string,
): { value: string | number; unit: string } | undefined => {
  const f = byCode.get(code)?.[0];
  if (!f) return undefined;
  const value = featureValue(f);
  if (value === undefined) return undefined;
  return { value, unit: featureUnit(f) };
};

const renderPhysicalProd = (features: RawFeature[], label: string): Attribute[] => {
  const byCode = groupByCode(features);
  const out: Attribute[] = [];
  const consumed = new Set<string>();
  const length = firstWithValue(byCode, 'prodLength');
  const height = firstWithValue(byCode, 'prodHeight');
  const depth = firstWithValue(byCode, 'prodDepth');
  if (length && height && depth) {
    const unit = length.unit || height.unit || depth.unit;
    const dims = `${length.value} x ${height.value} x ${depth.value}`;
    out.push({ label, value: unit ? `${dims} ${unit}` : dims });
    consumed.add('prodLength');
    consumed.add('prodHeight');
    consumed.add('prodDepth');
  }
  out.push(...renderDefault(features, consumed));
  return out;
};

const renderProdPackaged = (features: RawFeature[], label: string): Attribute[] => {
  const byCode = groupByCode(features);
  const out: Attribute[] = [];
  const consumed = new Set<string>();
  const length = firstWithValue(byCode, 'Packaged_LENGTH');
  const width = firstWithValue(byCode, 'Packaged_WIDTH');
  const height = firstWithValue(byCode, 'Packaged_HEIGHT');
  if (length && width && height) {
    const unit = length.unit || width.unit || height.unit;
    const dims = `${length.value} x ${width.value} x ${height.value}`;
    out.push({ label, value: unit ? `${dims} ${unit}` : dims });
    consumed.add('Packaged_LENGTH');
    consumed.add('Packaged_WIDTH');
    consumed.add('Packaged_HEIGHT');
  }
  out.push(...renderDefault(features, consumed));
  return out;
};

export const mapProductFeaturesToSpecifications = (parsed: unknown): Attribute[] => {
  if (!Array.isArray(parsed)) return [];
  return (parsed as RawGroup[]).flatMap((group) => {
    const features = Array.isArray(group?.features) ? group.features : [];
    // `categorycode` is the dispatch key (technical code, e.g. "Physical_PROD");
    // `classifyingcategory` is the user-facing label sourced from the same CT
    // group (e.g. "Product Dimensions") — no hardcoded strings here.
    const categoryCode = typeof group?.categorycode === 'string' ? group.categorycode : '';
    const label = typeof group?.classifyingcategory === 'string' ? group.classifyingcategory : '';
    if (categoryCode === PHYSICAL_PROD) return renderPhysicalProd(features, label);
    if (categoryCode === PROD_PACKAGED) return renderProdPackaged(features, label);
    return renderDefault(features);
  });
};
